import React, { useEffect, useMemo, useState } from 'react';
import { Building2, CheckCircle, Clock, CookingPot, PackageCheck, RefreshCw, XCircle } from 'lucide-react';
import { collection, doc, getDocs, onSnapshot, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import LoadingState from '../shared/LoadingState';
import { filterEstablishmentsForUser } from '../../utils/establishmentAccess';
import { buildOrderTrackingPayload, normalizePhoneKey } from '../../utils/orderTracking';
import { buildOrderStatusNotification, isOrderFinalizedToday, toLocalDateKey } from '../../utils/pointOfSale';
import './OrdersBoard.css';

const statuses = [
  { id: 'new', label: 'Novos', icon: Clock, next: 'preparing', action: 'Preparar' },
  { id: 'preparing', label: 'Em preparo', icon: CookingPot, next: 'ready', action: 'Pronto' },
  { id: 'ready', label: 'Prontos', icon: PackageCheck, next: 'delivered', action: 'Finalizar' },
  { id: 'delivered', label: 'Finalizados', icon: CheckCircle, next: null, action: '' }
];

const pointOfSaleTypeLabels = {
  bar: 'Bar',
  restaurant: 'Restaurante',
  kiosk: 'Quiosque'
};

const OrdersBoard = () => {
  const { userData } = useAuth();
  const [orders, setOrders] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [currentDay, setCurrentDay] = useState(() => toLocalDateKey(new Date()));

  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;

  useEffect(() => {
    if (!companyId) return;

    const loadEstablishments = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, `companies/${companyId}/establishments`));
        const data = snapshot.docs
          .map(item => ({ id: item.id, ...item.data() }))
          .filter(item => item.active !== false && ['bar', 'restaurant', 'kiosk'].includes(item.type))
          .sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bTime - aTime;
          });
        const allowed = filterEstablishmentsForUser(data, userData);
        setEstablishments(allowed);
        if (allowed.length === 1) {
          setSelectedEstablishmentId(allowed[0].id);
        } else if (selectedEstablishmentId && !allowed.some(item => item.id === selectedEstablishmentId)) {
          setSelectedEstablishmentId('');
        }
      } catch (error) {
        console.error('Erro ao carregar pontos de venda:', error);
        setEstablishments([]);
      } finally {
        setLoading(false);
      }
    };

    loadEstablishments();
  }, [companyId, userData, selectedEstablishmentId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentDay(toLocalDateKey(new Date()));
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;
    if (!selectedEstablishmentId) {
      setOrders([]);
      return undefined;
    }

    setLoadingOrders(true);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const activeOrdersQuery = query(
      collection(db, `companies/${companyId}/orders`),
      where('establishmentId', '==', selectedEstablishmentId),
      where('status', 'in', ['new', 'preparing', 'ready'])
    );
    const deliveredTodayQuery = query(
      collection(db, `companies/${companyId}/orders`),
      where('establishmentId', '==', selectedEstablishmentId),
      where('status', '==', 'delivered'),
      where('updatedAt', '>=', startOfToday)
    );
    let activeOrders = [];
    let deliveredOrders = [];
    const loadedSources = new Set();
    const applyOrders = () => {
      setOrders([...activeOrders, ...deliveredOrders].sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
      }));
      if (loadedSources.size === 2) setLoadingOrders(false);
    };
    const handleError = error => {
      console.error('Erro ao carregar pedidos:', error);
      setLoadingOrders(false);
    };
    const unsubscribeActive = onSnapshot(activeOrdersQuery, snapshot => {
      activeOrders = snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
      loadedSources.add('active');
      applyOrders();
    }, handleError);
    const unsubscribeDelivered = onSnapshot(deliveredTodayQuery, snapshot => {
      deliveredOrders = snapshot.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(order => isOrderFinalizedToday(order));
      loadedSources.add('delivered');
      applyOrders();
    }, handleError);

    return () => {
      unsubscribeActive();
      unsubscribeDelivered();
    };
  }, [companyId, selectedEstablishmentId, currentDay]);

  const selectedEstablishment = useMemo(() => (
    establishments.find(establishment => establishment.id === selectedEstablishmentId)
  ), [establishments, selectedEstablishmentId]);

  const filteredOrders = useMemo(() => {
    if (selectedStatus === 'active') {
      return orders.filter(order => order.status !== 'cancelled');
    }
    return orders.filter(order => order.status === selectedStatus);
  }, [orders, selectedStatus]);

  const groupedOrders = useMemo(() => {
    return statuses.reduce((groups, status) => {
      groups[status.id] = filteredOrders.filter(order => order.status === status.id);
      return groups;
    }, {});
  }, [filteredOrders]);

  const visibleStatuses = useMemo(() => {
    if (selectedStatus === 'active') {
      return statuses.filter(status => status.id !== 'delivered');
    }
    return statuses.filter(status => status.id === selectedStatus);
  }, [selectedStatus]);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

  const formatTime = (date) => {
    if (!date) return '--:--';
    const parsed = date.toDate ? date.toDate() : new Date(date);
    if (Number.isNaN(parsed.getTime())) return '--:--';
    return parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const updateStatus = async (order, status) => {
    const batch = writeBatch(db);
    const orderUpdates = {
      status,
      ...(status === 'delivered' ? { deliveredAt: serverTimestamp() } : {}),
      updatedAt: serverTimestamp()
    };
    batch.update(doc(db, `companies/${companyId}/orders`, order.id), orderUpdates);

    const phoneKey = normalizePhoneKey(order.customerPhone);
    if (phoneKey) {
      batch.set(
        doc(db, `companies/${companyId}/publicOrderStatus/${phoneKey}/orders`, order.id),
        buildOrderTrackingPayload(order, {
          ...orderUpdates,
          status,
          paymentStatus: order.paymentStatus || 'pending'
        }),
        { merge: true }
      );
    }

    const notification = buildOrderStatusNotification(order, status);
    if (notification) {
      batch.set(doc(db, `companies/${companyId}/userNotifications`, `${order.id}_${status}`), {
        ...notification,
        companyId,
        read: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    }

    try {
      await batch.commit();
    } catch (error) {
      console.error('Erro ao atualizar status do pedido:', error);
      alert('Não foi possível atualizar o pedido.');
    }
  };

  if (loading) {
    return <LoadingState title="Carregando pedidos" subtitle="Sincronizando fila da cozinha e bar" />;
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <div>
          <h1>Pedidos</h1>
          <p>{selectedEstablishment ? `Fila de ${selectedEstablishment.name}` : 'Selecione um ponto de venda para abrir a fila do tablet.'}</p>
        </div>
        {selectedEstablishmentId && (
          <div className="orders-filter">
            <button className={selectedStatus === 'active' ? 'active' : ''} onClick={() => setSelectedStatus('active')}>
              <RefreshCw size={17} />
              Ativos
            </button>
            {statuses.map(status => (
              <button key={status.id} className={selectedStatus === status.id ? 'active' : ''} onClick={() => setSelectedStatus(status.id)}>
                {status.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <section className="orders-establishments">
        <h2>Escolha o ponto de venda</h2>
        <div className="orders-establishment-grid">
          {establishments.map(establishment => (
            <button
              key={establishment.id}
              className={selectedEstablishmentId === establishment.id ? 'active' : ''}
              onClick={() => setSelectedEstablishmentId(establishment.id)}
            >
              <Building2 size={20} />
              <span>{establishment.name}</span>
              <small>{pointOfSaleTypeLabels[establishment.type] || 'Ponto de venda'}</small>
            </button>
          ))}
        </div>
        {establishments.length === 0 && (
          <p className="orders-empty">Cadastre um bar, restaurante ou quiosque em Pontos de venda para abrir a fila de pedidos.</p>
        )}
      </section>

      {!selectedEstablishmentId ? (
        <div className="orders-select-empty">
          <Building2 size={48} />
          <p>Selecione um ponto de venda acima para ver somente os pedidos dele.</p>
        </div>
      ) : loadingOrders ? (
        <LoadingState variant="block" title="Carregando fila" subtitle="Buscando pedidos do ponto de venda selecionado" />
      ) : (
      <div className="orders-board">
        {visibleStatuses.map(status => {
          const Icon = status.icon;
          const statusOrders = groupedOrders[status.id] || [];
          return (
            <section key={status.id} className="orders-column">
              <div className="orders-column-title">
                <Icon size={22} />
                <h2>{status.label}</h2>
                <span>{statusOrders.length}</span>
              </div>

              <div className="orders-list">
                {statusOrders.map(order => (
                  <article key={order.id} className="order-card">
                    <div className="order-card-header">
                      <div>
                        <strong>{order.customerName || 'Cliente'}</strong>
                        <span>{order.table || 'Sem mesa'} · {formatTime(order.createdAt)}</span>
                      </div>
                      <b>{formatCurrency(order.totalAmount)}</b>
                    </div>

                    <div className="order-establishment">{order.establishmentName || 'Ponto de venda'}</div>

                    <div className="order-items">
                      {(order.items || []).map((item, index) => (
                        <div key={`${item.menuItemId || item.name}-${index}`}>
                          <strong>{item.quantity}x</strong>
                          <span>{item.name}</span>
                        </div>
                      ))}
                    </div>

                    {order.notes && <p className="order-notes">{order.notes}</p>}

                    <div className="order-actions">
                      {status.next && (
                        <button onClick={() => updateStatus(order, status.next)}>
                          {status.action}
                        </button>
                      )}
                      {!['delivered', 'cancelled'].includes(order.status) && (
                        <button className="danger" onClick={() => updateStatus(order, 'cancelled')}>
                          <XCircle size={16} />
                          Cancelar
                        </button>
                      )}
                    </div>
                  </article>
                ))}

                {statusOrders.length === 0 && <p className="orders-empty">Sem pedidos nesta etapa.</p>}
              </div>
            </section>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default OrdersBoard;
